const STATUS_LABEL = {
  PreLancamento: "Pré-Lançamento",
  Lancamento: "Lançamento",
  EmConstrucao: "Últimas Unidades",
  Entregue: "Entregue",
  Vendido: "Vendido",
};

const STATUS_FILTER = {
  PreLancamento: "pre-lancamento",
  Lancamento: "lancamento",
  EmConstrucao: "ultimas-unidades",
  Entregue: "entregue",
  Vendido: "entregue",
};

export function slugify(text) {
  return (text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function mediaUrl(apiUrl, media) {
  return media?.url ? `${apiUrl}${media.url}` : undefined;
}

// Widest first; the original is appended after these.
const FORMAT_KEYS = ["small", "medium", "large", "xlarge"];

/**
 * Builds everything an <img> needs from a Strapi media object: a srcset across
 * the responsive formats, intrinsic dimensions, and the full-size URL for the
 * lightbox.
 *
 * Strapi only generates a format when it would actually shrink the image, so
 * the set is sparse and varies per record. A record with no formats at all
 * still yields a usable src plus dimensions, which is what keeps this safe for
 * anything uploaded before the backfill ran.
 *
 * Note this is orthogonal to format negotiation: srcset picks the resolution,
 * and the server picks AVIF/WebP/original from the Accept header.
 */
function mediaSource(apiUrl, media) {
  if (!media?.url) return undefined;

  const full = `${apiUrl}${media.url}`;
  const formats = media.formats || {};

  const candidates = [];
  for (const key of FORMAT_KEYS) {
    const format = formats[key];
    if (format?.url && format.width) {
      candidates.push({ url: `${apiUrl}${format.url}`, width: format.width });
    }
  }
  if (media.width) {
    candidates.push({ url: full, width: media.width });
  }

  // Same width from two sources would make the srcset ambiguous.
  const byWidth = {};
  candidates
    .sort((a, b) => a.width - b.width)
    .forEach((candidate) => {
      byWidth[candidate.width] = candidate;
    });
  const sorted = Object.keys(byWidth)
    .map(Number)
    .sort((a, b) => a - b)
    .map((width) => byWidth[width]);

  // Fallback for the no-srcset case: prefer a mid-size format over the original.
  const fallback = formats.large || formats.medium || formats.xlarge;

  return {
    src: fallback?.url ? `${apiUrl}${fallback.url}` : full,
    srcSet: sorted.length > 1 ? sorted.map((c) => `${c.url} ${c.width}w`).join(", ") : undefined,
    width: media.width,
    height: media.height,
    thumb: formats.thumbnail?.url ? `${apiUrl}${formats.thumbnail.url}` : undefined,
    full,
  };
}

function cidadeDisplay(enderecoCheio) {
  if (!enderecoCheio) return "";
  const { cidade, bairro, estado } = enderecoCheio;
  return [cidade, bairro && `Bairro ${bairro}`, estado].filter(Boolean).join(" · ");
}

// progressoGeral is the authoritative field once set in Strapi; until then,
// fall back to the average of the legacy multi-entry progresso[] list.
function progressPct(raw) {
  if (typeof raw.progressoGeral === "number") return raw.progressoGeral;
  if (raw.progresso?.length) {
    const total = raw.progresso.reduce((sum, p) => sum + (p.porcentagem || 0), 0);
    return Math.round(total / raw.progresso.length);
  }
  return 0;
}

export function mapPredio(raw, apiUrl) {
  const estado = raw.estado;
  const isVideo = raw.capa?.mime?.includes("video");

  return {
    id: raw.id,
    slug: slugify(raw.nomePredio),
    name: raw.nomePredio,

    badgeText: raw.badgeTexto || STATUS_LABEL[estado] || estado,
    statusClass: estado === "EmConstrucao" ? "status construcao" : "status",
    statusFilter: STATUS_FILTER[estado] || "",

    cidadeDisplay: cidadeDisplay(raw.enderecoCheio),
    cidadeFilter: slugify(raw.enderecoCheio?.cidade),

    cardImg: mediaSource(apiUrl, raw.cartao),
    heroImg: mediaSource(apiUrl, raw.imagemPrincipal),
    logoOverlay: mediaSource(apiUrl, raw.logo),

    chips: [raw.tipologia, raw.quartos, raw.vaga].filter(Boolean),
    beach: raw.distanciaPraia || undefined,
    dormValues: raw.dormitorios ? raw.dormitorios.split(",").map((v) => v.trim()) : [],
    finalidadeFilter: raw.finalidade || "",
    finalidadeDisplay:
      raw.finalidade === "investimento"
        ? "Investimento"
        : raw.finalidade === "moradia"
        ? "Moradia"
        : "",

    tipologia: raw.tipologia,
    quartos: raw.quartos,
    vaga: raw.vaga,

    progressPct: progressPct(raw),

    mapsQuery: raw.endereco,
    mapsZoom: 14,
    enderecoDisplay: raw.endereco,

    destaques: (raw.caracteristicas || []).map((c) => c.titulo).filter(Boolean),

    gallery: (raw.galeria || []).map((g) => ({
      img: mediaSource(apiUrl, g),
      caption: g.caption || g.alternativeText || g.name,
    })),

    plantas: (raw.planta || [])
      .filter((p) => p.planta)
      .map((p) => ({
        img: mediaSource(apiUrl, p.planta),
        caption: p.titulo,
      })),

    video: isVideo ? mediaUrl(apiUrl, raw.capa) : undefined,
    videoPoster: isVideo ? mediaUrl(apiUrl, raw.imagemPrincipal) : undefined,
  };
}

/**
 * `sizes` para uma imagem exibida com `object-fit: cover`.
 *
 * Numa caixa mais alta do que larga, o `cover` escala a imagem pela ALTURA e
 * corta as laterais -- a largura de arquivo necessária passa a ser
 * `altura da caixa × proporção da imagem`, não a largura da caixa. Declarar só
 * a largura da caixa (o intuitivo) faz o navegador baixar um arquivo pequeno
 * demais e ampliá-lo: é o que deixava os cards da listagem borrados, porque uma
 * foto 16:9 dentro de um card 286×520 precisa de ~925px de largura, e o `sizes`
 * pedia 300px.
 *
 * Como a proporção varia por imagem, o valor é calculado por imagem a partir
 * das dimensões que o Strapi devolve.
 */
export function coverSizes(img, { width, height, mobileWidth = 880 }) {
  // Caixa que ocupa a largura toda: `100vw` já descreve o lado maior, então vai
  // literal (não dá para comparar vw com px aqui).
  const trecho = (caixaLargura) => {
    if (typeof caixaLargura === "string") return caixaLargura;
    if (!img?.width || !img?.height) return `${caixaLargura}px`;
    return `${Math.max(
      caixaLargura,
      Math.ceil(height * (img.width / img.height))
    )}px`;
  };
  return `(max-width: 880px) ${trecho(mobileWidth)}, ${trecho(width)}`;
}

export function metaDescription(predio) {
  return `${predio.name}, ${predio.badgeText} em ${predio.cidadeDisplay}. ${
    predio.quartos || ""
  }, ${predio.vaga || ""}.`;
}

export function heroTag(predio) {
  return predio.gallery[0]?.caption || "Fachada — mais fotos em breve";
}
